import React from "react";
import CryptoJS from "crypto-js";
import axios from "axios";

import { 
  PlusCircleIcon, 
  PencilSquareIcon, 
  TrashIcon, 
  ArrowPathIcon, 
  Squares2X2Icon, 
  BarsArrowDownIcon, 
  BarsArrowUpIcon,
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/20/solid';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';



const EndPoint = process.env.REACT_APP_API_ENDPOINT;


/////////////////////////////////
//     HELPERS     //
/////////////////////////////////
// Problem  status
const PRIORITIES = ["high", "moderate", "low", "inactive"];
const PRIORITY_ORDER = { high: 0, moderate: 1, low: 2, inactive: 3 };
const PRIORITY_BG = {
  high: "bg-red-100",
  moderate: "bg-yellow-100",
  low: "bg-green-100",
  inactive: "bg-gray-100",
};

// Each entry status
const STATUS_NEXT = { "To do": "Doing", "Doing": "Done", "Done": "To do" };
const STATUS_ORDER = { "To do": 0, "Doing": 1, "Done": 2 };

const sortByStatus = (arr = []) =>
  arr.slice().sort((a, b) => {
    const sa = STATUS_ORDER[(a && a.status) || "To do"] ?? 0;
    const sb = STATUS_ORDER[(b && b.status) || "To do"] ?? 0;
    if (sa !== sb) return sa - sb;                          // To do → Doing → Done
    // tie-break: newest first (optional)
    return String(b?.dateAdded || "").localeCompare(String(a?.dateAdded || ""));
  });

// Tailwind-friendly background suggestions (accessible on light UI)
const STATUS_BG = {
  "To do": "bg-slate-200",   // calm grey
  "Doing": "bg-amber-300",   // attention yellow
  "Done":  "bg-emerald-200", // success green
};

// date utility
const formatDate = (iso) => {
  if (!iso) return "";
  const [y,m,d] = iso.split("-");
  return `${d}-${m}-${y}`;
};

const parseDate = (val) => {
  // input will come in as dd-mm-yyyy
  if (!val) return "";
  const [d,m,y] = val.split("-");
  return `${y}-${m}-${d}`;
};

const norm = (s) => (s || "").toLowerCase();

// ---- New data shapes ----
const todayISO = () => new Date().toISOString().slice(0, 10);
const authorFromState = (s) => s?.gpName?.trim() || "System";

const now = new Date();

// A list entry now: { title, dateAdded, author, status? }
const makeEntry = (title, s) => ({
  title: String(title || "").trim(),
  dateAdded: todayISO(),
  author: authorFromState(s),
  status: "To do", // default for action lists
});

// converts a string or object into a {title,dateAdded,author,status?} entry
const toEntry = (v, s, withStatus = false) => {
  if (v && typeof v === "object" && "title" in v) {
    return {
      title: String(v.title || "").trim(),
      dateAdded: v.dateAdded || todayISO(),
      author: v.author || authorFromState(s),
      status: withStatus ? (v.status || "To do") : undefined,
    };
  }
  const title = String(v || "").trim();
  if (!title) return null;
  return {
    title,
    dateAdded: todayISO(),
    author: authorFromState(s),
    status: withStatus ? "To do" : undefined,
  };
};

const arrToEntries = (arr, s, withStatus = false) =>
  (Array.isArray(arr) ? arr : [])
    .map((v) => toEntry(v, s, withStatus))
    .filter(Boolean);

// --- Schema / migration helpers ---
const SCHEMA_VERSION = 2;


// Normalize ONE condition (old/new shapes → new shape)
function normalizeCondition(c, s) {
  if (!c || typeof c !== "object") {
    return {
      problem: "",
      dateAdded: todayISO(),
      author: authorFromState(s),
      notes: [],
      goals: [],
      tasks: [],
      referrals: [],
      priority: "moderate",
      collapsed: false,
      schemaVersion: SCHEMA_VERSION,
    };
  }

  // problem can be string or { title }
  const problem =
    typeof c.problem === "string"
      ? c.problem.trim()
      : (c.problem && typeof c.problem === "object" && c.problem.title) || "";

  return {
    ...c,
    problem,
    dateAdded: c.dateAdded || todayISO(),
    author: c.author || authorFromState(s),
    // withStatus=false for notes, true for goals/tasks/referrals
    notes:     arrToEntries(c.notes, s, /*withStatus=*/false),
    goals:     arrToEntries(c.goals, s, /*withStatus=*/true),
    tasks:     arrToEntries(c.tasks, s, /*withStatus=*/true),
    referrals: arrToEntries(c.referrals, s, /*withStatus=*/true),
    priority: c.priority || "moderate",
    collapsed: !!c.collapsed,
    schemaVersion: SCHEMA_VERSION,
  };
}

// Normalize an ARRAY of conditions
function normalizeConditions(list, s) {
  return (Array.isArray(list) ? list : []).map((c) => normalizeCondition(c, s));
}


function makeBlankCondition(s) {
  return {
    id: crypto.randomUUID(),  
    problem: "",
    dateAdded: todayISO(),
    author: authorFromState(s),
    notes: [],
    goals: [],
    tasks: [],
    referrals: [],
    priority: "moderate",
    collapsed: false,
    schemaVersion: SCHEMA_VERSION,
  };
}


/////////////////////////////////
//     COMPONENTS SECTION      //
/////////////////////////////////

const IconButton = ({ label, title, onClick, children, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative group rounded-xl px-3 py-2 text-white bg-sky-800 hover:bg-sky-900 focus:outline-none focus:ring-2 focus:ring-sky-400 ${className}`}
    aria-label={label}
    title={title || ""} // fallback native tooltip
  >
    {children}
    {/* Custom tooltip (no state) */}
    <span
      role="tooltip"
      className="
        pointer-events-none
        absolute -top-10 left-1/2 -translate-x-1/2
        whitespace-nowrap rounded bg-slate-700 p-2 text-sm text-white shadow
        opacity-0 translate-y-1
        transition-opacity transition-transform duration-150
        group-hover:opacity-100 group-hover:translate-y-0
        group-focus-visible:opacity-100 group-focus-visible:translate-y-0
      "
    >
      {label}
    </span>
  </button>
);

class TextField extends React.PureComponent {
  render() {
    const { label, value, onChange, type = "text", placeholder, className = "", ...rest } = this.props;
    return (
      <label className={`block ${className}`}>
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <input
          type={type}
          className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/30"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          {...rest}
        />
      </label>
    );
  }
}

class TextArea extends React.PureComponent {
  render() {
    const { label, value, onChange, rows = 4, placeholder, className = "" } = this.props;
    return (
      <label className={`block ${className}`}>
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <textarea
          rows={rows}
          className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/30"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    );
  }
}

class DateField extends React.PureComponent {
  render() {
    const { label, value, onChange, className = "" } = this.props;
    return (
      <TextField
        label={label}
        type="text"
        className={className}
        placeholder="dd-mm-yyyy"
        value={formatDate(value)}                // show dd-mm-yyyy
        onChange={(v) => onChange(parseDate(v))} // store yyyy-mm-dd
      />
    );
  }
}

class ListEditorColumn extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { draft: "", editIndex: -1, editDraft: "" };
  }

  // ===== time helpers =====
  getNowISO = () =>
    typeof todayISO === "function" ? todayISO() : new Date().toISOString().slice(0, 10);
  getNowMs = () => Date.now();

  // Prefer ms fields; fall back to parse-able timestamps; else 0
  getDateMs = (it) => {
    if (!it) return 0;
    if (typeof it.updatedAtMs === "number") return it.updatedAtMs;
    if (typeof it.createdAtMs === "number") return it.createdAtMs;
    const iso = it.updatedAt || it.date || it.dateAdded;
    const t = iso ? Date.parse(iso) : NaN;
    return Number.isFinite(t) ? t : 0;
  };

  // Build a view list: [{ it, idx }...] and (optionally) sort for display only
  buildView = () => {
    const { items = [], sortViewByDate } = this.props;
    const withIndex = items.map((it, idx) => ({ it, idx }));
    if (!sortViewByDate) return withIndex;

    const dir = String(sortViewByDate || "desc").toLowerCase(); // "desc" default
    const sgn = dir === "asc" ? 1 : -1;

    return withIndex.slice().sort((a, b) => {
      const ad = this.getDateMs(a.it);
      const bd = this.getDateMs(b.it);
      if (ad !== bd) return sgn * (ad - bd);
      // tie-breaker: keep stable relative order of original array
      return a.idx - b.idx;
    });
  };

  // ===== edit flow =====
  startEdit = (idx, current) => {
    this.setState({ editIndex: idx, editDraft: current?.title || "" });
  };

  cancelEdit = () => this.setState({ editIndex: -1, editDraft: "" });

  confirmEdit = () => {
    const { items = [], onChange, getAuthor } = this.props;
    const { editIndex, editDraft } = this.state;
    if (editIndex < 0) return;

    const next = items.slice();
    const prior = next[editIndex] || {};
    const nowISO = this.getNowISO();
    const nowMs = this.getNowMs();

    next[editIndex] = {
      ...prior,
      title: String(editDraft || "").trim(),
      dateAdded: prior.dateAdded || nowISO,
      author: prior.author || (getAuthor ? getAuthor() : "System"),
      updatedAt: nowISO,
      updatedAtMs: nowMs,
      createdAtMs: typeof prior.createdAtMs === "number" ? prior.createdAtMs : nowMs,
    };

    const cleaned = next.filter((x) => x && x.title);
    onChange(cleaned);
    this.setState({ editIndex: -1, editDraft: "" });
  };

  // ===== add / delete / status =====
  addItem = () => {
    const { items = [], onChange, getAuthor, enableStatus } = this.props;
    const v = (this.state.draft || "").trim();
    if (!v) return;

    const nowISO = this.getNowISO();
    const nowMs = this.getNowMs();

    const entry = {
      title: v,
      dateAdded: nowISO,        // shown as dd-mm-yyyy via formatDate elsewhere
      updatedAt: nowISO,
      createdAtMs: nowMs,       // for sorting (creation)
      updatedAtMs: nowMs,       // for sorting (last change)
      author: getAuthor ? getAuthor() : "System",
      ...(enableStatus ? { status: "To do" } : {}),
    };

    onChange([...items, entry]);
    this.setState({ draft: "" });
  };

  deleteItem = (idxOriginal) => {
    const { items = [], onChange } = this.props;
    const next = items.slice();
    next.splice(idxOriginal, 1);
    onChange(next);
    if (this.state.editIndex === idxOriginal) this.cancelEdit();
  };

  toggleStatus = (idxOriginal) => {
    const { items = [], onChange, enableStatus } = this.props;
    if (!enableStatus) return;

    const next = items.slice();
    const cur = next[idxOriginal] || {};
    const curStatus = cur.status || "To do";
    const nowISO = this.getNowISO();
    const nowMs = this.getNowMs();

    next[idxOriginal] = {
      ...cur,
      status: STATUS_NEXT[curStatus],
      updatedAt: nowISO,
      updatedAtMs: nowMs,
      createdAtMs: typeof cur.createdAtMs === "number" ? cur.createdAtMs : nowMs,
    };

    onChange(next);
  };

  render() {
    const { label, enableStatus } = this.props;
    const { draft, editIndex, editDraft } = this.state;
    const view = this.buildView(); // [{ it, idx }]

    return (
      <div className="rounded shadow bg-white p-3">
        <div className="text-md text-white text-center bg-slate-500 font-medium mb-2 p-2 rounded-md">
          {label}
        </div>

        <div className="space-y-2">
          {(!view || view.length === 0) && (
            <div className="text-xs text-gray-400">No entries yet.</div>
          )}

          {view.map(({ it, idx: originalIdx }, viewRowIdx) => (
            <div
              key={it?.id ?? `${originalIdx}-${it?.dateAdded ?? ""}-${viewRowIdx}`}
              className="group relative rounded-lg border border-gray-200 px-3 py-2 bg-gray-100 shadow hover:bg-gray-100"
            >
              {editIndex === originalIdx ? (
                <div className="space-y-2">
                  <textarea
                    rows={2}
                    className="w-full rounded-lg border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-black/30"
                    value={editDraft}
                    onChange={(e) => this.setState({ editDraft: e.target.value })}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="rounded-lg border px-2 py-1 text-xs hover:bg-gray-50"
                      onClick={this.confirmEdit}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border px-2 py-1 text-xs hover:bg-gray-50"
                      onClick={this.cancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="pr-16 text-sm whitespace-pre-wrap">
                    {it?.title || ""}
                    {(it?.updatedAt || it?.dateAdded) && (
                      <p className="text-[10px] text-gray-500">
                        {it?.updatedAt
                          ? `Updated ${formatDate(it.updatedAt)}`
                          : `Added ${formatDate(it.dateAdded)}`}
                      </p>
                    )}
                  </div>

                  <div className="absolute right-1 top-1 flex flex-col gap-1 transition">
                    <div className="flex gap-1">
                      <button
                        type="button"
                        className="rounded-md border px-2 py-0.5 text-xs bg-white"
                        title="Edit"
                        onClick={() => this.startEdit(originalIdx, it)}
                      >
                        <PencilSquareIcon className="w-4 h-4 text-slate-700" />
                      </button>
                      <button
                        type="button"
                        className="rounded-md border px-2 py-0.5 text-xs bg-white"
                        title="Delete"
                        onClick={() => this.deleteItem(originalIdx)}
                      >
                        <TrashIcon className="w-4 h-4 text-slate-700" />
                      </button>
                    </div>

                    {enableStatus && (
                      <button
                        type="button"
                        onClick={() => this.toggleStatus(originalIdx)}
                        className={`text-xs px-2 pt-0.5 pb-1 rounded-md border ${STATUS_BG[it?.status || "To do"]}`}
                        title="Click to cycle status"
                      >
                        {it?.status || "To do"}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="mt-3">
          <textarea
            rows={2}
            className="w-full rounded-lg border border-gray-200 resize-none px-2 py-1 focus:outline-none focus:ring-2 focus:ring-black/30"
            placeholder="Add new…"
            value={draft}
            onChange={(e) => this.setState({ draft: e.target.value })}
          />
          <div className="mt-2">
            <button
              type="button"
              className="rounded shadow w-full border px-3 py-1 text-sm hover:bg-sky-100"
              onClick={this.addItem}
            >
              Add
            </button>
          </div>
        </div>
      </div>
    );
  }
}


class ConditionCard extends React.PureComponent {
  constructor(props){
    super(props);
    this.state = { 
      showMenu: false,
      editingProblem: false,   
      draftProblem: props.data.problem || "",
      fullLibrary: []
    };
    this.blurTimer = null;
  }

  componentDidMount() {
    this.fetchLibrary();

    // Initialise collapsed state from collapseAll if explicitly provided
    if (this.props.collapseAll === "true") {
      const { data, onUpdate, collapseAll } = this.props;
      if (data.collapsed !== collapseAll) {
        onUpdate({ ...data, collapsed: collapseAll }, { shouldSort: false });
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.collapseAll !== this.props.collapseAll) {
      const { data, onUpdate } = this.props;
      onUpdate({ ...data, collapsed: this.props.collapseAll }, { shouldSort: false });
    }

    if (prevProps.collapseAll !== this.props.collapseAll) {
      const desired = !!this.props.collapseAll; // true = collapse, false = expand
      const { data, onUpdate } = this.props;
      if (!!data.collapsed !== desired) {
        onUpdate({ ...data, collapsed: desired }, { shouldSort: false });
      }
    }
  }

  
  fetchLibrary = async () => {
    try {
      const res = await axios.get(`${EndPoint}/api/library`);
      // Transform library documents to CONDITION_DB shape
      const libraryData = res.data.map(item => ({
        name: item.text || "Untitled Condition",
        goals: (item.goals || []).map(g => g.entry),
        tasks: (item.todos || []).map(t => t.entry),
        referrals: (item.referrals || []).map(r => r.entry)
      }));


      this.setState({fullLibrary: libraryData})

    } catch (err) {
      console.error("❌ Failed to fetch library:", err);
      return [];
    }
  }

  handleProblemChange = (v) => {
    const { data, onUpdate } = this.props;
    onUpdate({ ...data, problem: v });
  };

  selectSuggestion = (cond) => {
    const { data, onUpdate } = this.props;
    const s = this.props.pageStateGetter?.() || {};

    const next = {
      ...data,
      problem: cond.name,
      dateAdded: data.dateAdded || todayISO(),
      author: data.author || authorFromState(s),
      goals: (cond.goals || []).map((g) => makeEntry(g, s)),
      tasks: (cond.tasks || []).map((t) => makeEntry(t, s)),
      referrals: (cond.referrals || []).map((r) => makeEntry(r, s)),
    };

    onUpdate(next);
    this.setState({ showMenu: false, editingProblem: false, draftProblem: cond.name });
  };

  // inside ConditionCard
  togglePriority = () => {
    const { data, onUpdate } = this.props;
    const cur = (data.priority || "moderate").toLowerCase();
    const i = PRIORITIES.indexOf(cur);
    const next = PRIORITIES[(i + 1) % PRIORITIES.length];
    onUpdate({ ...data, priority: next }, { shouldSort: false }); // ⬅️ no sort
  };

  setPriority = (p) => {
    const { data, onUpdate } = this.props;
    onUpdate({ ...data, priority: p }, { shouldSort: false }); // ⬅️ no sort
  };


  toggleCollapsed = () => {

    const { data, onUpdate } = this.props;
    onUpdate({ ...data, collapsed: !data.collapsed });
  };


  renderPriorityPills(current) {
    return (
      <div className="flex gap-1 items-center">Priority : 
        {PRIORITIES.map(p => {
          const active = current === p;
          return (
            <button
              key={p}
              type="button"
              className={`px-2 py-1 text-xs rounded-full border transition ${
                active ? "border-black bg-sky-800 text-white font-semibold" : "border-gray-300 text-gray-700 hover:border-gray-400"
              }`}
              onClick={() => this.setPriority(p)}
              title={`Set priority: ${p}`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          );
        })}
      </div>
    );
  }


  render() {
    const { data, onUpdate, onRemove } = this.props;
    const s = this.props.pageStateGetter?.() || {};

    // normalize the incoming condition (handles old/new shapes)
    const safe = normalizeCondition(data, s);
    const { notes, goals, tasks, referrals } = safe;

    const collapsed = !!safe.collapsed;

    const query = this.state.editingProblem ? this.state.draftProblem : safe.problem;

    const filterConditions = (query) => {
      const fromLibrary = this.state.fullLibrary
      const q = norm(query);
      if (q.length < 2) return [];
      return fromLibrary.filter((c) => norm(c.name).includes(q)).slice(0, 8);
    };
    const suggestions = filterConditions(query);
    const priority = safe.priority || "moderate";
    const bg = PRIORITY_BG[priority] || "bg-white";

    return (
      <div className={`rounded ${bg} p-2 shadow hover:shadow-lg hover:transform hover:ring-2 hover:ring-sky-800`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {this.state.editingProblem ? (
              <input
                type="text"
                autoFocus
                className="mt-1 mr-2 w-full rounded shadow px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/30"
                value={this.state.draftProblem}
                placeholder="Start typing condition..."
                onChange={(e) => {
                  const v = e.target.value;
                  this.setState({ draftProblem: v, showMenu: v.length >= 2 });
                }}
                onFocus={() => this.setState({ showMenu: (this.state.draftProblem || "").length >= 2 })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    this.props.onUpdate({
                      ...normalizeCondition(safe, s),
                      problem: this.state.draftProblem,
                      dateAdded: safe.dateAdded || todayISO(),
                      author: safe.author || authorFromState(s),
                    });
                    this.setState({ editingProblem: false, showMenu: false });
                  }
                  if (e.key === "Escape") {
                    this.setState({ editingProblem: false, draftProblem: safe.problem, showMenu: false });
                  }
                }}
                onBlur={() => {
                  const page = this.props.pageStateGetter?.();
                  this.props.onUpdate({
                    ...normalizeCondition(safe, page),
                    problem: this.state.draftProblem,
                    dateAdded: safe.dateAdded || todayISO(),
                    author: safe.author || authorFromState(page),
                  });
                  this.blurTimer = setTimeout(() => this.setState({ showMenu: false }), 120);
                }}
              />
            ) : (
              <div
                className="mt-1 mr-2 w-full px-3 py-2 cursor-pointer text-xl font-semibold text-slate-800"
                onDoubleClick={() => {
                  const isNew = !safe.problem || safe.problem.trim() === "";
                  this.setState({
                    editingProblem: true,
                    draftProblem: isNew ? "" : safe.problem,
                    showMenu: (isNew ? "" : safe.problem).length >= 2,
                  });
                }}
              >
                {safe.problem || <span className="text-gray-400">Double-click here to add condition…</span>}
                {safe.problem && (
                  <div className="text-[10px] text-gray-500">
                    Added {formatDate(safe.dateAdded)} 
                  </div>
                )}
              </div>
            )}
          </div>

            <button
            type="button"
            onClick={this.togglePriority}
            className="px-2 py-1 mr-4 text-xs rounded border border-gray-300 bg-white hover:bg-white/60"
            title={`Priority: ${priority}. Click to change`}
          >
            {priority.charAt(0).toUpperCase() + priority.slice(1)}
          </button>

          <button
            type="button"
            onClick={this.toggleCollapsed}
            className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-white/60"
            title={collapsed ? "Expand" : "Collapse"}
            
          >
            {collapsed ? (
              <ChevronDoubleDownIcon className="w-5 h-5" />
            ) : (
              <ChevronDoubleUpIcon className="w-5 h-5" />
            )}
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="ml-2 text-sm underline text-red-600 hover:text-red-700"
          >
          <TrashIcon className="w-5 h-5 text-red-400" />
          </button>
        </div>

        {/* Suggestions dropdown */}
        <div className="mb-1 relative">
          {this.state.showMenu && suggestions.length > 0 && (
            <div className="absolute z-20 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg max-h-56 overflow-auto">
              {suggestions.map((cond, i) => (
                <button
                  key={i}
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                  onMouseDown={(e) => e.preventDefault()} // keep input from blurring
                  onClick={() => this.selectSuggestion(cond)}
                >
                  {cond.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Expandable body */}
        {!collapsed && (
          <>

            <div className="grid grid-cols-4 md:grid-cols-4 gap-4 sm:grid-cols-1 xs:grid-cols-1">
              <ListEditorColumn
                label="Notes"
                items={notes}
                enableStatus={false}
                getAuthor={() => authorFromState(this.props.pageStateGetter?.())}
                onChange={(next) => onUpdate({ ...safe, notes: next })}
                sortViewByDate="desc" 
              />
              <ListEditorColumn
                label="SMART Goals"
                items={goals}
                enableStatus={true}
                getAuthor={() => authorFromState(this.props.pageStateGetter?.())}
                onChange={(next) => onUpdate({ ...safe, goals: next })}
              />
              <ListEditorColumn
                label="Tasks, required treatments and services"
                items={tasks}
                enableStatus={true}
                getAuthor={() => authorFromState(this.props.pageStateGetter?.())}
                onChange={(next) => onUpdate({ ...safe, tasks: next })}
              />
              <ListEditorColumn
                label="Arrangements and referrals"
                items={referrals}
                enableStatus={true}
                getAuthor={() => authorFromState(this.props.pageStateGetter?.())}
                onChange={(next) => onUpdate({ ...safe, referrals: next })}
              />
            </div>
          </>
        )}
      </div>
    );
  }

}

class Toggle extends React.PureComponent {
  render() {
    const { label, checked, onChange, className = "" } = this.props;
    return (
      <label className={`flex items-center gap-2 ${className}`}>
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="text-sm text-gray-800">{label}</span>
      </label>
    );
  }
}

class Tabs extends React.PureComponent {
  render() {
    const { value, onChange, items = [] } = this.props;

    const leftTabs = items.filter(
      (it) => !["mhcp", "ha", "gpccmp", "consultation"].includes(it.value)
    );

    const rightTabs = items.filter(
      (it) => ["mhcp", "ha", "gpccmp", "consultation"].includes(it.value)
    );

    const renderTab = (it) => {
      const active = value === it.value;

      return (
        <button
          key={it.value}
          role="tab"
          aria-selected={active}
          className={`px-3 py-2 text-sm rounded-t-xl transition 
            ${
              active
                ? "bg-white border-x border-t border-gray-200 font-semibold"
                : "text-gray-600 hover:text-black"
            }`}
          onClick={() => onChange(it.value)}
        >
          {it.label}
        </button>
      );
    };

    return (
      <div
        role="tablist"
        aria-label="Left pane tabs"
        className="flex flex-wrap items-end gap-2 border-b border-gray-200 mb-4 "
      >
        {/* Left side tabs */}
        {leftTabs.map(renderTab)}

        {/* Right side tabs */}
        <div className="ml-32 sm:ml-20 xs:-ml-0 ">
          {rightTabs.map(renderTab)}
        </div>
      </div>
    );
  }
}

class SignaturePad extends React.PureComponent {
  constructor(props){
    super(props);
    this.canvasRef = React.createRef();
    this.ctx = null;
    this.drawing = false;
    this.last = null;
  }

  componentDidMount(){ this.setupCanvas(); window.addEventListener('resize', this.setupCanvas); }
  componentWillUnmount(){ window.removeEventListener('resize', this.setupCanvas); }
  componentDidUpdate(prev){
    if (prev.width !== this.props.width || prev.height !== this.props.height) this.setupCanvas();
  }

  setupCanvas = () => {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const cssW = this.props.width || canvas.clientWidth || 400;
    const cssH = this.props.height || 160;
    canvas.width = cssW * ratio;
    canvas.height = cssH * ratio;
    canvas.style.width = cssW + "px";
    canvas.style.height = cssH + "px";
    this.ctx = canvas.getContext("2d");
    this.ctx.scale(ratio, ratio);
    this.ctx.lineCap = "round";
    this.ctx.lineJoin = "round";
    this.ctx.lineWidth = 2;
    this.ctx.strokeStyle = "#111827"; // gray-900
    // redraw from value if provided
    if (this.props.value) this.loadImage(this.props.value);
    else this.clear(false);
  };

  loadImage = (dataUrl) => {
    const canvas = this.canvasRef.current;
    if (!canvas || !dataUrl) return;
    const img = new Image();
    img.onload = () => {
      this.clear(false);
      this.ctx.drawImage(img, 0, 0, canvas.clientWidth, canvas.clientHeight);
    };
    img.src = dataUrl;
  };

  pointer = (e) => {
    const rect = this.canvasRef.current.getBoundingClientRect();
    const x = (e.clientX ?? (e.touches && e.touches[0].clientX)) - rect.left;
    const y = (e.clientY ?? (e.touches && e.touches[0].clientY)) - rect.top;
    return { x, y };
  };

  onPointerDown = (e) => {
    e.preventDefault();
    this.drawing = true;
    this.last = this.pointer(e);
  };

  onPointerMove = (e) => {
    if (!this.drawing) return;
    const p = this.pointer(e);
    this.ctx.beginPath();
    this.ctx.moveTo(this.last.x, this.last.y);
    this.ctx.lineTo(p.x, p.y);
    this.ctx.stroke();
    this.last = p;
  };

  onPointerUp = (e) => {
    if (!this.drawing) return;
    this.drawing = false;
    this.last = null;
    // push value up
    const dataUrl = this.canvasRef.current.toDataURL("image/png");
    this.props.onChange && this.props.onChange(dataUrl);
  };

  clear = (emit = true) => {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    this.ctx.fillStyle = "#ffffff";
    this.ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight);
    if (emit && this.props.onChange) this.props.onChange("");
  };

  render(){
    return (
      <div className="border rounded-xl bg-slate-100 mt-2 p-2 w-96">
        <div
          className="px-3 py-2 text-sm text-gray-600 flex items-center justify-between"
        >
          <span>Draw signature</span>
          <button
            type="button"
            className="rounded-lg border bg-sky-800 hover:bg-sky-900 px-2 py-1 text-xs text-white"
            onClick={() => this.clear(true)}
          >
            Clear
          </button>
        </div>
        <canvas
          ref={this.canvasRef}
          className="w-full h-40 touch-none cursor-crosshair rounded-b-xl"
          onMouseDown={this.onPointerDown}
          onMouseMove={this.onPointerMove}
          onMouseUp={this.onPointerUp}
          onMouseLeave={this.onPointerUp}
          onTouchStart={this.onPointerDown}
          onTouchMove={this.onPointerMove}
          onTouchEnd={this.onPointerUp}
        />
      </div>
    );
  }
}

const todaysDate = new Date();
const proposedReviewDate = new Date(todaysDate);
proposedReviewDate.setDate(proposedReviewDate.getDate() + 85);

/////////////////////////////////
//    Main Component           //
/////////////////////////////////

const STORAGE_KEY = "patientData";
const SECRET_KEY = "keepEverythingRocks";

class GPCCMP extends React.Component {

    getBlankState = () => ({
        // Header
        practiceLetterhead: "",
        gpName: "",

        // Patient
        ptFullName: "",
        ptMCNo: "",
        ptDoB: "",
        isATSI: "",
        ptPhoneMob: "",
        ptPhoneH: "",
        ptStreet: "",
        ptCity: "",
        ptPostcode: "",
        ptState: "",
        usualGP: "",

        // Carer
        carerName: "",
        carerRelationship: "",
        carerPhone: "",

        // Dates
        lastGPCCMPDate: "",
        todaysDate: todaysDate.toISOString().slice(0, 10),
        proposedReviewDate: proposedReviewDate.toISOString().slice(0, 10),

        // Notes
        otherNotes: "",
        selectedPMH: "",
        selectedRx: "",
        reactions: "",
        socialHx: "",
        familyHx: "",
        todaysVisit: "",

        // MHCP
        mhcp: {},
        mhformulation: {},
        mhsafety: {},
        relapsePrevnetion: {},

        // HA
        healthAssessment:  {},

        // Consultation
        consultations: [],

        //localStorage
        patientData: {},
        loadedFromStorage: false,

        // Tabs
        activeMainTab: "patient",

        // Plan conditions
        conditions: [
        ],

        // Post-service
        copyOfferedToPatient: false,
        addedToRecords: false,
        copyToProviders: false,
        consentShareHistory: false,
        consentUploadMyHR: false,
        withheldInfo: "",

        // Signature
        gpSignatureDataUrl: "", 

        // File status
        hasDocOpen: false,

        // Smart paste
        smartPasteOpen: false,
        smartPasteText: "",
        smartPasteBusy: false,
        smartPasteError: "",

        // Tooltip
        showPaste : false,
        showAdd : false,
        showConsult: false, 

        // Consultation Note popup state
        consultationNoteOpen: false,
        consultationNote: "",
        consultationAI: '',

        // Documents
        documents: [],                 
        selectedDocIndex: -1,

        // Add Doc modal state
        addDocOpen: false,
        addDocTitle: "",
        addDocType: "Pathology",
        addDocDate: new Date().toISOString().slice(0, 10),
        addDocTags: [],            
        addDocContent: "",
        addDocEditingIndex: -1, 

        // Documents quick-paste modal
        docPasteOpen: false,
        docPasteText: "",
        docPasteBusy: false,

        // Collapse all
        collapseAll : false,

        // summary text
        todaysSummaryText: "",

        // initial load
        userData: {},
        creditsBalance: null,
        hasSubscription: null,
        
        // gpccmp tab
        activeTab : "Plan",

        // ===== SERVER CHECK (access gate) =====
        accessLoading: true,
        accessError: "",
        accessAllowed: false,
        pageId: "",
        leadEmail: "",
    });


    constructor(props) {
      super(props);

        this.toastId = null;
        this.state = this.getBlankState();
        this.blank = this.getBlankState(); 
        // Password dialog state
        this._pwResolve = null;   
        this._pwReject = null;    
        this.pwInputRef = React.createRef();
        this.state.passwordDialogOpen = false;
        this.state.passwordDialogTitle = "";
        this.state.passwordDialogMessage = "";
        this.state.passwordDialogValue = "";
        this.state.passwordDialogError = "";
        this.state.passwordDialogOkText = "OK";
        this.state.passwordDialogCancelText = "Cancel";
        this.state.mergeOpen = false;
        this.state.mergeTitle = "";
        this.state.mergeSelected = new Set();
    }

    
    //////////////////////////////////
    //  LOCAL STORAGE              ///
    //////////////////////////////////

    // --- Encryption helpers ---
    encrypt = (obj) => {
      const plaintext = JSON.stringify(obj || {});
      return CryptoJS.AES.encrypt(plaintext, SECRET_KEY).toString();
    };

    decrypt = (cipherText) => {
      try {
        const bytes = CryptoJS.AES.decrypt(cipherText, SECRET_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        if (!decrypted) return null;
        return JSON.parse(decrypted);
      } catch (err) {
        console.error("Failed to decrypt patientData:", err);
        return null;
      }
    };

    getLocalStorageSnapshot = () => {
      const {
        practiceLetterhead, gpName,
        ptFullName, ptMCNo, ptDoB, isATSI,
        ptPhoneMob, ptPhoneH, ptStreet, ptCity, ptPostcode, ptState, usualGP,
        carerName, carerRelationship, carerPhone,
        lastGPCCMPDate, todaysDate, proposedReviewDate,
        otherNotes, selectedPMH, selectedRx, reactions, socialHx, familyHx, todaysVisit,
        conditions, documents, todaysSummaryText,
        mhcp, mhformulation, mhsafety, relapsePrevnetion,
        healthAssessment,
        consultations, 
        copyOfferedToPatient, addedToRecords, copyToProviders,
        consentShareHistory, consentUploadMyHR, withheldInfo,
        gpSignatureDataUrl,
      } = this.state;

      return {
        practiceLetterhead, gpName,
        ptFullName, ptMCNo, ptDoB, isATSI,
        ptPhoneMob, ptPhoneH, ptStreet, ptCity, ptPostcode, ptState, usualGP,
        carerName, carerRelationship, carerPhone,
        lastGPCCMPDate, todaysDate, proposedReviewDate,
        otherNotes, selectedPMH, selectedRx, reactions, socialHx, familyHx, todaysVisit,
        conditions, documents, todaysSummaryText,
        mhcp, mhformulation, mhsafety, relapsePrevnetion,
        healthAssessment,
        consultations,
        copyOfferedToPatient, addedToRecords, copyToProviders,
        consentShareHistory, consentUploadMyHR, withheldInfo,
        gpSignatureDataUrl,
      };
    };

    // --- Save / load / clear localStorage ---

    saveDataToLocalStorage = () => {
      try {
        const snapshot = this.getLocalStorageSnapshot();
        const encrypted = this.encrypt(snapshot);
        localStorage.setItem(STORAGE_KEY, encrypted);
      } catch (err) {
        console.error("Failed to save GPCCMP draft:", err);
      }
    };


    loadDataFromLocalStorage = () => {
      try {
        const encrypted = localStorage.getItem(STORAGE_KEY);
        if (!encrypted) {
          console.log("No GPCCMP draft in localStorage");
          return;
        }

        const data = this.decrypt(encrypted);
        if (data) {
          const normalized = {
            ...this.state, // keep credits, subscription, etc
            ...data,
            conditions: normalizeConditions(data.conditions || [], data),
            hasDocOpen: true,
            loadedFromStorage: true,
          };
          this.setState(normalized);
        }
      } catch (err) {
        console.error("Failed to load GPCCMP draft:", err);
      }
    };

    clearLocalStorage = () => {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (err) {
        console.error("Failed to clear localStorage:", err);
      }
    };

    setStateAndSave = (updater, cb) => {
      this.setState(
        typeof updater === "function" ? updater : () => updater,
        () => {
          this.saveDataToLocalStorage();   // 🔐 write encrypted snapshot
          if (typeof cb === "function") cb();
        }
      );
    };



    //////////////////////////////////
    //  LOADING COMPONENT          ///
    //////////////////////////////////

    componentDidMount = async() => {
      this.checkAccessOnServer();
      this.setState((s) => ({
        conditions: normalizeConditions(s.conditions, s),
      }));

      this.loadDataFromLocalStorage();
    }

      getQueryParam = (key) => {
    try {
      const sp = new URLSearchParams(window.location.search || "");
      return sp.get(key) || "";
    } catch {
      return "";
    }
  };

  /* =========================
    PAGE CHECKER
  ========================= */

  checkAccessOnServer = async () => {
    const pageId = this.getQueryParam("id");

    // No id = no access
    if (!pageId) {
      this.setState({
        accessLoading: false,
        accessAllowed: false,
        accessError: "Missing link ID. Please open the tool from your email link.",
        pageId: "",
      });
      return;
    }

    this.setState({ accessLoading: true, accessError: "", pageId });

    try {
      // IMPORTANT: include credentials so cookies are sent/received
      const r = await fetch(
        `${EndPoint}/api/magic/page/visit?id=${encodeURIComponent(pageId)}`,
        { method: "GET", credentials: "include" }
      );
      const data = await r.json().catch(() => ({}));

      if (!r.ok || !data || data.ok === false) {
        this.setState({
          accessLoading: false,
          accessAllowed: false,
          accessError: data?.error || "Invalid or expired link.",
        });
        return;
      }

      // Decide your gate rule:
      // Option A (strict): must be email verified
      const allowed = !!data.isEmailVerified;

      if (!allowed) {
        this.setState({
          accessLoading: false,
          accessAllowed: false,
          accessError:
            "Your email is not verified yet. Please click the verification link in your email, then refresh this page.",
          leadEmail: data.email || "",
        });
        return;
      }

      // Allowed
      this.setState({
        accessLoading: false,
        accessAllowed: true,
        accessError: "",
        leadEmail: data.email || "",
      });

      // Optional: auto-open a blank doc when allowed
      // this.handleNew();

    } catch (err) {
      console.error("checkAccessOnServer failed:", err);
      this.setState({
        accessLoading: false,
        accessAllowed: false,
        accessError: "Network error. Please refresh and try again.",
      });
    }
  };


    //////////////////////////////////
    //  DATA HANDLING              ///
    //////////////////////////////////
    addCondition = () => {
      this.setState((s) => {
        if (s.conditions.length >= 12) return s;
        const blank = makeBlankCondition(s);
        const nextConds = [blank, ...(s.conditions || [])];
        return {
          conditions: nextConds
        };
      }, () => {
        this.saveDataToLocalStorage();
      });
    };

    updateCondition = (idx, next, { shouldSort = false } = {}) => {
      this.setState((s) => {
        const conditions = s.conditions.slice();
        conditions[idx] = next;

        const finalConditions = shouldSort ? this.sortConditions(conditions) : conditions;


        return { conditions: finalConditions };
      }, () => {
        this.saveDataToLocalStorage();
      });
    };

    removeCondition = (idx) => {
      this.setState((s) => {
        const conditions = s.conditions.slice();
        conditions.splice(idx, 1);
        const safe = conditions.length ? conditions : [];
        const sorted = this.sortConditions(safe);
        return {
          conditions: sorted
        };
      }, () => {
        this.saveDataToLocalStorage();
      });
    };

    getAvailableTags = () => {
      const names = (this.state.conditions || []).map(c => c?.problem || "").filter(Boolean);
      // unique + trimmed
      return Array.from(new Set(names.map(s => s.trim()).filter(Boolean)));
    };


    exportJSON = () => {
        const dataStr = JSON.stringify(this.state, null, 2);
        const blob = new Blob([dataStr], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `GPCCMP_${this.state.ptFullName || "patient"}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    printPlan = () => {
        window.print();
    };

    //////////////////////////////////
    //  PATIENT FILE HANDLING      ///
    //////////////////////////////////

    // Suggested filename using patient's name or JohnDoe
    getSuggestedFilename = () => {
    const name = (this.state.ptFullName || "JohnDoe")
        .trim()
        .replace(/[^\w\-]+/g, "_"); // safe-ish filename
    return `${name}.cdv`;
    };

    // Serialize everything (you can prune fields here if needed)
    serializeState = () => JSON.stringify(this.state);

    // Simple AES encrypt/decrypt with a passphrase (CryptoJS handles salt)
    showPasswordDialog = ({
      title = "Enter Password",
      message = "",
      okText = "OK",
      cancelText = "Cancel",
      defaultValue = "",
      validate, // optional: (value) => string|undefined (error message)
    } = {}) => {
      // Close any existing dialog promise
      if (this._pwReject) {
        try { this._pwReject(new Error("Superseded")); } catch {}
      }

      return new Promise((resolve, reject) => {
        this._pwResolve = resolve;
        this._pwReject = reject;
        this.setState(
          {
            passwordDialogOpen: true,
            passwordDialogTitle: title,
            passwordDialogMessage: message,
            passwordDialogOkText: okText,
            passwordDialogCancelText: cancelText,
            passwordDialogValue: defaultValue,
            passwordDialogError: "",
          },
          () => {
            // focus the input as soon as it mounts
            setTimeout(() => this.pwInputRef.current?.focus(), 0);
          }
        );

        // simple handlers that we’ll call from the UI
        this._confirmPasswordDialog = () => {
          const v = (this.state.passwordDialogValue || "").trim();
          if (!v) {
            this.setState({ passwordDialogError: "Password cannot be empty." });
            return;
          }
          if (typeof validate === "function") {
            const err = validate(v);
            if (err) {
              this.setState({ passwordDialogError: err });
              return;
            }
          }
          this.setState({ passwordDialogOpen: false }, () => {
            this._pwResolve?.(v);
            this._pwResolve = null;
            this._pwReject = null;
          });
        };

        this._cancelPasswordDialog = () => {
          this.setState({ passwordDialogOpen: false }, () => {
            this._pwResolve?.(null); // user cancelled
            this._pwResolve = null;
            this._pwReject = null;
          });
        };
      });
    };

    encryptState = (passphrase) => {
    const plaintext = this.serializeState();
    return CryptoJS.AES.encrypt(plaintext, passphrase).toString();
    };

    decryptState = (ciphertext, passphrase) => {
    const bytes = CryptoJS.AES.decrypt(ciphertext, passphrase);
    const json = bytes.toString(CryptoJS.enc.Utf8);
    if (!json) throw new Error("Decryption failed. Wrong password or corrupt file.");
    return JSON.parse(json);
    };

    saveEncryptedCDV = async () => {
        try {
          const pass = await this.showPasswordDialog({
          title: "Encrypt & Save",
          message: "Enter a password to encrypt this file.",
          okText: "Encrypt & Save",
        });
        if (pass == null) return; // user cancelled
        const cipher = this.encryptState(pass);
        const suggestedName = this.getSuggestedFilename();

        // Preferred: File System Access API
        if (window.showSaveFilePicker) {
        const handle = await window.showSaveFilePicker({
            suggestedName,
            types: [
            {
                description: "Caredevo Encrypted File",
                accept: { "application/x-caredevo": [".cdv"] },
            },
            ],
            excludeAcceptAllOption: false,
        });
        const writable = await handle.createWritable();
        await writable.write(cipher);
        await writable.close();
        return;
        }

        // Fallback: trigger a download
        const blob = new Blob([cipher], { type: "application/x-caredevo" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = suggestedName;
        a.click();
        URL.revokeObjectURL(url);
    } catch (err) {
        console.error(err);
        window.alert(`Save failed: ${err.message || err}`);
    }
    };

    handleNew = () => {
      this.setState((prev) => {
        const blank = this.getBlankState();

        return {
          ...blank,
          hasDocOpen: true,
          conditions: [],
          // ✅ preserve access gate
          accessLoading: prev.accessLoading,
          accessAllowed: prev.accessAllowed,
          accessError: prev.accessError,
          pageId: prev.pageId,
          leadEmail: prev.leadEmail,

          // ✅ preserve these too (you already do)
          creditsBalance: prev.creditsBalance,
          hasSubscription: prev.hasSubscription,
          userData: prev.userData,
        };
      }, () => {
        this.clearLocalStorage();
      });
    };

    handleClose = () => {
      this.setState((prev) => {
        const blank = this.getBlankState();

        return {
          ...blank,
          hasDocOpen: false,
          conditions: [],
          // ✅ preserve access gate
          accessLoading: prev.accessLoading,
          accessAllowed: prev.accessAllowed,
          accessError: prev.accessError,
          pageId: prev.pageId,
          leadEmail: prev.leadEmail,

          creditsBalance: prev.creditsBalance,
          hasSubscription: prev.hasSubscription,
          userData: prev.userData,
        };
      }, () => {
        this.clearLocalStorage();
      });
    };


    loadEncryptedCDV = async () => {
      try {
        // Preferred: File System Access API
        if (window.showOpenFilePicker) {
          const [handle] = await window.showOpenFilePicker({
            multiple: false,
            types: [
              {
                description: "Caredevo Encrypted File",
                accept: { "application/x-caredevo": [".cdv"] },
              },
            ],
            excludeAcceptAllOption: false,
          });
          const file = await handle.getFile();
          const text = await file.text();
          const pass = await this.showPasswordDialog({
            title: "Open Encrypted File",
            message: "Enter the password to decrypt.",
            okText: "Decrypt & Open",
          });
          if (pass == null) return;
          const data = this.decryptState(text, pass);

          // 🔁 normalize incoming conditions (backward compatibility)
          const normalized = {
            ...data,
            conditions: normalizeConditions(data.conditions, data),
            hasDocOpen: true,
          };
          this.setState(normalized);
          return;
        }

        // Fallback: use a hidden file input
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".cdv,application/x-caredevo";
        input.onchange = async () => {
          const file = input.files && input.files[0];
          if (!file) return;
          const text = await file.text();
          const pass = window.prompt("Enter the password to decrypt:");
          if (!pass) return;
          try {
            const data = this.decryptState(text, pass);
            const normalized = {
              ...data,
              conditions: normalizeConditions(data.conditions, data),
              hasDocOpen: true,
            };
            this.setState(normalized);
          } catch (err) {
            console.error(err);
            window.alert(`Fail loading file: ${err.message || err}`);
          }
        };
        input.click();
      } catch (err) {
        console.error(err);
        window.alert(`Fail loading file: ${err.message || err}`);
      }
    };


    sortConditions = (arr = []) => {
      const pri = (c) =>
        (!c?.problem ? -1 : (PRIORITY_ORDER?.[c.priority] ?? PRIORITY_ORDER?.moderate ?? 1));
      return arr.slice().sort((a, b) => pri(a) - pri(b));
    };

    copyPreviewToClipboard = async (type) => {
      var htmlDoc;
      try {
        if (type === 'All') {
           htmlDoc = this.getAllWordHTML();
        } else {
            htmlDoc = this.getWordHTML();
        }
  
        const plain = document.getElementById("print-root")?.innerText || "";

        if (navigator.clipboard && window.ClipboardItem) {
          await navigator.clipboard.write([
            new window.ClipboardItem({
              "text/html": new Blob([htmlDoc], { type: "text/html" }),
              "text/plain": new Blob([plain],   { type: "text/plain" }),
            }),
          ]);
          window.alert("Copied with table formatting. Paste into Word with Ctrl/Cmd+V.");
          return;
        }
        // Fallbacks
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(plain);
          window.alert("Copied as plain text (browser doesn’t allow HTML clipboard).");
          return;
        }
        const ta = document.createElement("textarea");
        ta.value = plain; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        window.alert("Copied as plain text.");
      } catch (e) {
        console.error(e);
        window.alert("Copy failed (clipboard permissions?).");
      }
    };

    getWordHTML = () => {
      const s = this.state;
      const sorted = this.sortConditions(s.conditions);

      const esc = (x) => String(x ?? "")
        .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
      const br = (x) => esc(x).replace(/\r?\n/g, "<br/>");
      const toArr = (v) =>
        Array.isArray(v)
          ? v
              .filter(Boolean)
              .map((x) => (typeof x === "string" ? x : x?.title || ""))
              .filter(Boolean)
          : String(v || "").split(/\r?\n/).map((t) => t.trim()).filter(Boolean);


      // Inline style snippets
      const base = 'font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif; font-size:8pt; line-height:1.3; color:#111; ' +
                  'mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;';
      /* A4 page width = 8.27in. Word default margins ≈ 1in each side ⇒ usable width ≈ 6.27in.
        Use 6.2in to be safe. */
      const tbl  = 'border-collapse:collapse; mso-table-lspace:0pt; mso-table-rspace:0pt; ' +
                  'table-layout:fixed; width:6.8in; max-width:100%; margin:6pt 0 10pt;';
      const th   = 'border:1px solid #ccc; padding:6pt; vertical-align:top; ' +
                  'font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt; font-weight:600; background:#f3f4f6; ' +
                  'word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;';
      const td   = 'border:1px solid #ccc; padding:6pt; vertical-align:top; ' +
                  'font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt; ' +
                  'word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;';
      const row  = 'margin:0 0 6pt 0; font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;';


      // Render list as rows; include explicit size on each row + inner span
      const list = (arr) => {
        // If array of objects (new schema): drop "Done", then sort; render titles.
        if (Array.isArray(arr) && arr.length && typeof arr[0] === "object") {
          const filtered = arr.filter(x => (x?.status || "To do") !== "Done");
          const ordered = sortByStatus(filtered);
          const titles = ordered.map(x => (typeof x === "string" ? x : x?.title || "")).filter(Boolean);
          return titles.length
            ? titles.map(i => `<div style="${row}"><span style="font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;">${esc(i)}</span></div>`).join("")
            : "";
        }

        // Fallback (string arrays / old schema)
        const a = toArr(arr);
        return a.length
          ? a.map(i => `<div style="${row}"><span style="font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;">${esc(i)}</span></div>`).join("")
          : "";
      };

      const text = (t) =>
        `<span style="font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;">${esc(t)}</span>`;

      return `
    <!doctype html>
    <html>
    <head><meta charset="utf-8"></head>
    <body>
      <!-- Wrap everything in <font size="1"> as a belt-and-braces fallback Word respects -->
      <font size="1">
        <div style="${base}">
        
        <h2 style="text-align:center">Chronic Condition Management Plan</h2>
          <table border="0" cellspacing="0" cellpadding="0" style="${tbl}">
            <colgroup>
              <col style="width:22%">
              <col style="width:26%">
              <col style="width:26%">
              <col style="width:26%">
            </colgroup>
         
            <tr>
              <th style="${th}">${text('Condition/Problem')}</th>
              <th style="${th}">${text('SMART Goals')}</th>
              <th style="${th}">${text('Tasks, required treatments and services')}</th>
              <th style="${th}">${text('Arrangements and referrals')}</th>
            </tr>
            ${sorted.map(c => `
              <tr>
                <td style="${td}">${text(c.problem)}
                  <div style="font-size:6pt; margin-left:2px; line-height:1.1;">${list(c.notes)}</div>
                </td>
                <td style="${td}">${list(c.goals)}</td>
                <td style="${td}">${list(c.tasks)}</td>
                <td style="${td}">${list(c.referrals)}</td>
              </tr>
            `).join("")}
          </table>
          <p></p>
        <p><b>Acknowledgements & Consents</b></p>
        <ul>
          <li>Copy of GPCCMP offered to patient: ${s.copyOfferedToPatient ? "Yes" : "No"}</li>
          <li>GPCCMP added to the patient’s records: ${s.addedToRecords ? "Yes" : "No"}</li>
          <li>Copy of relevant parts supplied to other providers: ${s.copyToProviders ? "Yes" : "No"}</li>
          <li>The patient consents to share history/diagnosis with other providers: ${s.consentShareHistory ? "Yes" : "No"}</li>
          <li>Patient consents to upload to My Health Record: ${s.consentUploadMyHR ? "Yes" : "No"}</li>
          <li>Proposed review date : ${esc(formatDate(s.proposedReviewDate))} </li>
        </ul>
               
        <p><b>Date:</b> ${esc(formatDate(s.todaysDate))} </p>
        <b>Signature</b>
      
        </p>
        ${s.gpSignatureDataUrl ? `<img src="${s.gpSignatureDataUrl}" alt="Signature" 
        style="display:block;
                 width:80pt;
                 height:auto;
                 margin:0;
                 border:0;" />` : "_______________________________"
        }</p>
          <p><b>GP Name:</b> ${esc(s.gpName)}</p>
    
        <p></p>
        <b>Any information the patient wants withheld</b>
        <div>${br(s.withheldInfo)}</div>
        </div>
      </font>
    </body>
    </html>`;
    };

    getAllWordHTML = () => {
      const s = this.state;
      const sorted = this.sortConditions(s.conditions);

      const esc = (x) => String(x ?? "")
        .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
      const br = (x) => esc(x).replace(/\r?\n/g, "<br/>");
      const toArr = (v) =>
        Array.isArray(v)
          ? v
              .filter(Boolean)
              .map((x) => (typeof x === "string" ? x : x?.title || ""))
              .filter(Boolean)
          : String(v || "").split(/\r?\n/).map((t) => t.trim()).filter(Boolean);


      // Inline style snippets
      const base = 'font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif; font-size:8pt; line-height:1.3; color:#111; ' +
                  'mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;';
      /* A4 page width = 8.27in. Word default margins ≈ 1in each side ⇒ usable width ≈ 6.27in.
        Use 6.2in to be safe. */
      const tbl  = 'border-collapse:collapse; mso-table-lspace:0pt; mso-table-rspace:0pt; ' +
                  'table-layout:fixed; width:6.8in; max-width:100%; margin:6pt 0 10pt;';
      const th   = 'border:1px solid #ccc; padding:6pt; vertical-align:top; ' +
                  'font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt; font-weight:600; background:#f3f4f6; ' +
                  'word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;';
      const td   = 'border:1px solid #ccc; padding:6pt; vertical-align:top; ' +
                  'font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt; ' +
                  'word-wrap:break-word; overflow-wrap:anywhere; word-break:break-word;';
      const row  = 'margin:0 0 6pt 0; font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;';
      const addr = [s.ptStreet, s.ptCity, s.ptPostcode, s.ptState].filter(Boolean).join(" ");

      // Render list as rows; include explicit size on each row + inner span
      const list = (arr) => {
        // If array of objects (new schema): drop "Done", then sort; render titles.
        if (Array.isArray(arr) && arr.length && typeof arr[0] === "object") {
          const filtered = arr.filter(x => (x?.status || "To do") !== "Done");
          const ordered = sortByStatus(filtered);
          const titles = ordered.map(x => (typeof x === "string" ? x : x?.title || "")).filter(Boolean);
          return titles.length
            ? titles.map(i => `<div style="${row}"><span style="font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;">${esc(i)}</span></div>`).join("")
            : "";
        }

        // Fallback (string arrays / old schema)
        const a = toArr(arr);
        return a.length
          ? a.map(i => `<div style="${row}"><span style="font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;">${esc(i)}</span></div>`).join("")
          : "";
      };


      const text = (t) =>
        `<span style="font-size:8pt; mso-ansi-font-size:8.0pt; mso-bidi-font-size:8.0pt;">${esc(t)}</span>`;

      return `
    <!doctype html>
    <html>
    <head><meta charset="utf-8"></head>
    <body>
      <!-- Wrap everything in <font size="1"> as a belt-and-braces fallback Word respects -->
      <font size="1">
        <div style="${base}">
              <div style="text-align:center; font-weight:700; white-space:pre-wrap">${br(s.practiceLetterhead)}</div>
        <h1 style="text-align:center;">GP CHRONIC CONDITION MANAGEMENT PLAN</h1>
        <div class="muted" style="text-align:center;">MBS Item 965/92029 or 392/92060</div>

        <h2>Patient Details</h2>
        <table class="noborder">
          <tr><td><b>Name:</b> ${esc(s.ptFullName)}</td><td><b>Medicare No:</b> ${esc(s.ptMCNo)}</td></tr>
          <tr><td><b>Date of Birth:</b> ${esc(formatDate(s.ptDoB))}</td><td><b>Aboriginal or Torres Strait Islander:</b> ${esc(s.isATSI)}</td></tr>
          <tr><td><b>Phone (M):</b> ${esc(s.ptPhoneMob)}</td><td><b>Phone (H):</b> ${esc(s.ptPhoneH)}</td></tr>
          <tr><td colspan="2"><b>Address:</b> ${esc(addr)}</td></tr>
          <tr><td colspan="2"><b>Usual GP:</b> ${esc(s.usualGP)}</td></tr>
        </table>

        <h2>Carer (if applicable)</h2>
        <table class="noborder">
          <tr><td><b>Name:</b> ${esc(s.carerName)}</td><td><b>Relationship:</b> ${esc(s.carerRelationship)}</td><td><b>Phone:</b> ${esc(s.carerPhone)}</td></tr>
        </table>

        <h2>Timing</h2>
        <table class="noborder">
          <tr>
            <td><b>Date of last GPCCMP/Review:</b> ${esc(formatDate(s.lastGPCCMPDate))}</td>
            <td><b>Date service completed:</b> ${esc(formatDate(s.todaysDate))}</td>
            <td><b>Proposed Review Date:</b> ${esc(formatDate(s.proposedReviewDate))}</td>
          </tr>
        </table>

        <h2>Other Notes</h2>
        <div>${br(s.otherNotes)}</div>

        <h2>Histories & Notes</h2>
        <table>
          <tr><th>Medical/Surgical History</th><th>Medications</th></tr>
          <tr><td>${br(s.selectedPMH)}</td><td>${br(s.selectedRx)}</td></tr>
          <tr><th>Allergies</th><th>Social History</th></tr>
          <tr><td>${br(s.reactions)}</td><td>${br(s.socialHx)}</td></tr>
          <tr><th>Family History</th><th>Current Progress Notes</th></tr>
          <tr><td>${br(s.familyHx)}</td><td>${br(s.todaysVisit)}</td></tr>
        </table>
        
        <h2 style="text-align:center">Chronic Condition Management Plan</h2>
          <table border="0" cellspacing="0" cellpadding="0" style="${tbl}">
            <colgroup>
              <col style="width:22%">
              <col style="width:26%">
              <col style="width:26%">
              <col style="width:26%">
            </colgroup>
         
            <tr>
              <th style="${th}">${text('Condition/Problem')}</th>
              <th style="${th}">${text('SMART Goals')}</th>
              <th style="${th}">${text('Tasks, required treatments and services')}</th>
              <th style="${th}">${text('Arrangements and referrals')}</th>
            </tr>
            ${sorted.map(c => `
              <tr>
                <td style="${td}">${text(c.problem)}
                  <div style="font-size:6pt; margin-left:2px; line-height:1.1;">${list(c.notes)}</div>
                </td>
                <td style="${td}">${list(c.goals)}</td>
                <td style="${td}">${list(c.tasks)}</td>
                <td style="${td}">${list(c.referrals)}</td>
              </tr>
            `).join("")}
          </table>
          <p></p>
        <p><b>Acknowledgements & Consents</b></p>
        <ul>
          <li>Copy of GPCCMP offered to patient: ${s.copyOfferedToPatient ? "Yes" : "No"}</li>
          <li>GPCCMP added to the patient’s records: ${s.addedToRecords ? "Yes" : "No"}</li>
          <li>Copy of relevant parts supplied to other providers: ${s.copyToProviders ? "Yes" : "No"}</li>
          <li>The patient consents to share history/diagnosis with other providers: ${s.consentShareHistory ? "Yes" : "No"}</li>
          <li>Patient consents to upload to My Health Record: ${s.consentUploadMyHR ? "Yes" : "No"}</li>
        </ul>
               
        <p><b>Date:</b> ${esc(formatDate(s.todaysDate))}</p>
        <b>Signature</b>
      
        </p>
        ${s.gpSignatureDataUrl ? `<img src="${s.gpSignatureDataUrl}" alt="Signature" 
        style="display:block;
                 width:80pt;
                 height:auto;
                 margin:0;
                 border:0;" />` : "_______________________________"
        }</p>
          <p><b>GP Name:</b> ${esc(s.gpName)}</p>
    
        <p></p>
        <b>Any information the patient wants withheld</hb>
        <div>${br(s.withheldInfo)}</div>
        </div>
      </font>
    </body>
    </html>`;
    };
  
    //////////////////////////////////
    // TOOLS                       ///
    //////////////////////////////////

    // Merge functions
    openMergeModal = () => {
      this.setState({ mergeOpen: true, mergeSelected: new Set(), mergeTitle: "" });
    };

    closeMergeModal = () => {
      this.setState({ mergeOpen: false });
    };

    toggleMergeSelect = (idx) => {
      this.setState((s) => {
        const next = new Set(s.mergeSelected);
        if (next.has(idx)) next.delete(idx);
        else next.add(idx);
        return { mergeSelected: next };
      });
    };

    // helper: pick the highest-priority among selected (lowest numeric index in PRIORITY_ORDER)
    _pickMergedPriority = (conds = []) => {
      const idx = (p) => (PRIORITY_ORDER?.[p] ?? PRIORITY_ORDER?.moderate ?? 1);
      let bestKey = "moderate";
      let best = Infinity;
      for (const c of conds) {
        const v = idx(c?.priority);
        if (v < best) { best = v; bestKey = c?.priority || "moderate"; }
      }
      return bestKey;
    };

    // helper: merge arrays of strings/objects (dedupe by title/text, keep first object instance)
    _mergeActionLists = (...lists) => {
      const out = [];
      const seen = new Set();
      for (const arr of lists) {
        for (const e of (Array.isArray(arr) ? arr : [])) {
          const title = typeof e === "string" ? e.trim() : String(e?.title || "").trim();
          if (!title) continue;
          const key = title.toLowerCase();
          if (seen.has(key)) continue;
          seen.add(key);
          out.push(typeof e === "string" ? title : { ...e });
        }
      }
      return out;
    };

    performMerge = () => {
      const { mergeTitle, mergeSelected, conditions } = this.state;
      const name = (mergeTitle || "").trim();

      if (!name) {
        toast.error("Please enter a Merge Into title.");
        return;
      }
      if (!mergeSelected || mergeSelected.size === 0) {
        toast.error("Select at least one problem to merge.");
        return;
      }

      // Collect chosen conditions (keep original order)
      const selectedIdx = Array.from(mergeSelected).sort((a, b) => a - b);
      const picked = selectedIdx.map((i) => conditions[i]).filter(Boolean);

      // Build merged condition
      const merged = {
        problem: name,
        priority: this._pickMergedPriority(picked),
        notes: this._mergeActionLists(...picked.map(c => c?.notes || [])),
        goals: this._mergeActionLists(...picked.map(c => c?.goals || [])),
        tasks: this._mergeActionLists(...picked.map(c => c?.tasks || [])),
        referrals: this._mergeActionLists(...picked.map(c => c?.referrals || [])),
        author: "Merged",
        dateAdded: (typeof todayISO === "function") ? todayISO() : new Date().toISOString().slice(0,10),
      };

      // Remove picked and insert merged at the top
      const remaining = conditions.filter((_, i) => !mergeSelected.has(i));
      const next = [merged, ...remaining];

      this.setState({
        conditions: next,
        mergeOpen: false,
        mergeSelected: new Set(),
        mergeTitle: "",
      }, () => {
        toast.success("Problems merged.");
      });
      this.saveDataToLocalStorage();
    };

    collapseAll = () => {
      this.setState((s) => {
        const nextFlag = !s.collapseAll;
        return {
          collapseAll: nextFlag,
          conditions: (s.conditions || []).map(c => ({ ...c, collapsed: nextFlag })),
        };
      }, () => {
        this.saveDataToLocalStorage();
      });
    };

    appendLine = (prev, line) => {
      if (!line?.trim()) return prev;
      const trimmed = line.trim();
      // optional de-dupe:
      if (prev.split("\n").some(l => l.trim() === trimmed)) return prev;
      return prev ? `${prev}\n${trimmed}` : trimmed;
    };

    // callback passed down to children
    handleEmitSummaryLine = (idx, line) => {
      this.setState(prev => ({
        todaysSummaryText: this.appendLine(prev.todaysSummaryText, line)
      }));
    };

    resortAll = () => {
      this.setState((s) => {
        const sortedConditions = this.sortConditions(
          (s.conditions || []).map((c) => ({
            ...c,
            goals: sortByStatus(c?.goals || []),
            tasks: sortByStatus(c?.tasks || []),
            referrals: sortByStatus(c?.referrals || []),
          }))
        );
        return {
          conditions: sortedConditions
        };
      }, () => {
        this.saveDataToLocalStorage();
      });
    };

    //////////////////////////////////
    //  RENDER DIFFERENT SECTION   ///
    //////////////////////////////////
    renderPatientDataTab() {
      const s = this.state;
      return (
          <div className="space-y-6">
    
          <div className='grid grid-cols-2 gap-4 xs:grid-cols-1 sm:grid-cols-1'>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">Patient Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <TextField label="Name" value={s.ptFullName} onChange={(v)=>this.setStateAndSave({ptFullName:v})} id='joy-Name'/>
              <TextField label="Medicare No" value={s.ptMCNo} onChange={(v)=>this.setStateAndSave({ptMCNo:v})} />
              <DateField  label="Date of Birth" value={s.ptDoB} onChange={(v)=>this.setStateAndSave({ptDoB:v})} />
              <TextField label="Identifies as Aboriginal or Torres Strait Islander" value={s.isATSI} onChange={(v)=>this.setStateAndSave({isATSI:v})} placeholder="Yes/No / prefer not to say" />
              <TextField label="Phone (Mobile)" value={s.ptPhoneMob} onChange={(v)=>this.setStateAndSave({ptPhoneMob:v})} />
              <TextField label="Phone (Home)" value={s.ptPhoneH} onChange={(v)=>this.setStateAndSave({ptPhoneH:v})} />
              <TextField className="md:col-span-2" label="Street" value={s.ptStreet} onChange={(v)=>this.setStateAndSave({ptStreet:v})} />
              <TextField label="City/Suburb" value={s.ptCity} onChange={(v)=>this.setStateAndSave({ptCity:v})} />
              <TextField label="Postcode" value={s.ptPostcode} onChange={(v)=>this.setStateAndSave({ptPostcode:v})} />
              <TextField label="State" value={s.ptState} onChange={(v)=>this.setStateAndSave({ptState:v})} />
              <TextField label="Usual GP" value={s.usualGP} onChange={(v)=>this.setStateAndSave({usualGP:v})} />
              </div>
          </div>
          <div className='grid grid-cols-1 gap-2'>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">Practice & GP</h2>
              <TextArea label="Practice Letterhead" value={s.practiceLetterhead} onChange={(v)=>this.setStateAndSave({practiceLetterhead:v})} rows={2} />
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <TextField label="GP Name" value={s.gpName} onChange={(v)=>this.setStateAndSave({gpName:v})} />
              <div className="flex items-end text-sm text-gray-600">MBS Item 965/92029 or 392/92060</div>
              </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">Carer Details (if applicable)</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <TextField label="Carer Name" value={s.carerName} onChange={(v)=>this.setStateAndSave({carerName:v})} />
              <TextField label="Relationship to Patient" value={s.carerRelationship} onChange={(v)=>this.setStateAndSave({carerRelationship:v})} />
              <TextField label="Carer Phone" value={s.carerPhone} onChange={(v)=>this.setStateAndSave({carerPhone:v})} />
              </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">Timing</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <DateField  label="Date of last GPCCMP/Review" value={s.lastGPCCMPDate} onChange={(v)=>this.setStateAndSave({lastGPCCMPDate:v})} />
              <DateField  label="Date service was completed" value={s.todaysDate} onChange={(v)=>this.setStateAndSave({todaysDate:v})} />
              <DateField  label="Proposed Review Date" value={s.proposedReviewDate} onChange={(v)=>this.setStateAndSave({proposedReviewDate:v})} />
              </div>
          </div>
          </div>
          </div>
          </div>
      );
    }

    renderHistoryTab() {
      const s = this.state;
      return (
          <div className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="mb-4 text-lg font-semibold">Histories & Notes</h2>
              <TextArea label="Other notes or comments relevant to the patient’s management plan" value={s.otherNotes} onChange={(v)=>this.setStateAndSave({otherNotes:v})} />
              <div className="mt-3 grid grid-cols-2 xs:grid-cols-1 sm:grid-cols-1 gap-3">
              <TextArea rows='8' label="Medical/Surgical History" value={s.selectedPMH} onChange={(v)=>this.setStateAndSave({selectedPMH:v})} />
              <TextArea rows='8' label="Medications" value={s.selectedRx} onChange={(v)=>this.setStateAndSave({selectedRx:v})} />
              <TextArea rows='8' label="Allergies" value={s.reactions} onChange={(v)=>this.setStateAndSave({reactions:v})} />
              <TextArea rows='8' label="Social History" value={s.socialHx} onChange={(v)=>this.setStateAndSave({socialHx:v})} />
              <TextArea rows='8' label="Family History" value={s.familyHx} onChange={(v)=>this.setStateAndSave({familyHx:v})} />
              <TextArea rows='8' label="Current Progress Notes" value={s.todaysVisit} onChange={(v)=>this.setStateAndSave({todaysVisit:v, todaysVisit:v})} />
              </div>
          </div>
          </div>
      );
    }

    renderPlanTab() {
      const s = this.state;
      const list = s.conditions || [];


      return (
          <div className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="text-3xl font-semibold text-center">Action Plan</h2>
            <div className="mb-4 flex items-center justify-center space-x-4  xs:flex-col">
              <div className='flex flex-row gap-4 mt-4'>
                <IconButton label={this.state.collapseAll ? "Expand All" : "Collapse All"} onClick={this.collapseAll}>
                  {this.state.collapseAll ? <BarsArrowDownIcon className="w-8 h-8" /> : <BarsArrowUpIcon className="w-8 h-8" /> }
                </IconButton>

                <IconButton label="Add Condition" onClick={this.addCondition}>
                  <PlusCircleIcon className="w-8 h-8" />
                </IconButton>

                <IconButton label="Refresh ordering" onClick={this.resortAll}>
                  <ArrowPathIcon className="w-8 h-8" />
                </IconButton>

                <IconButton label="Merge problems" onClick={this.openMergeModal}>
                  <Squares2X2Icon className="w-8 h-8" />
                </IconButton>
              </div>
   
            </div>

              <div className="space-y-4">
                {list.map((c, i) => (
                  <ConditionCard
                    key={c.id}
                    idx={i}
                    data={c}
                    autoFocus={i === 0}  
                    pageStateGetter={() => this.state}  
                    onUpdate={(next)=>this.updateCondition(i, next)}
                    onRemove={()=>this.removeCondition(i)}
                    collapseAll={this.state.collapseAll}

                  />
                ))}
              </div>

          </div>
          </div>
      );
    }

    renderConsentTab() {
    const s = this.state;
    return (
        <div className="space-y-6">
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold">Acknowledgements & Consents</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Toggle label="Copy of GPCCMP offered to patient" checked={s.copyOfferedToPatient} onChange={(v)=>this.setState({copyOfferedToPatient:v})} />
            <Toggle label="GPCCMP added to the patient’s records" checked={s.addedToRecords} onChange={(v)=>this.setState({addedToRecords:v})} />
            <Toggle label="Copy of relevant parts supplied to other providers" checked={s.copyToProviders} onChange={(v)=>this.setState({copyToProviders:v})} />
            <Toggle label="Consent: Share history/diagnosis with providers" checked={s.consentShareHistory} onChange={(v)=>this.setState({consentShareHistory:v})} />
            <Toggle label="Consent: Upload plan to My Health Record" checked={s.consentUploadMyHR} onChange={(v)=>this.setState({consentUploadMyHR:v})} />
            </div>

            <TextArea className="mt-3" label="Any information the patient wants withheld" value={s.withheldInfo} onChange={(v)=>this.setState({withheldInfo:v})} />

            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <TextField label="GP Name" value={s.gpName} onChange={(v)=>this.setState({gpName:v})} />
             <DateField  label="Signature Date" value={s.todaysDate} onChange={(v)=>this.setState({todaysDate:v})} />
            </div>
             <div>
              <SignaturePad
                value={s.gpSignatureDataUrl}
                onChange={(dataUrl)=>this.setState({ gpSignatureDataUrl: dataUrl })}
              />
              {s.gpSignatureDataUrl && (
                <div className="mt-2 text-xs text-gray-500">Saved as PNG in document state.</div>
              )}
            </div>
        </div>
        </div>
    );
    }

    renderPreviewPanel() {
      const s = this.state;
      const sortedConds = this.sortConditions(s.conditions);

      // helper to render a list with status ordering (for goals/tasks/referrals)
      const renderActionList = (arr) =>
        (Array.isArray(arr) ? arr : []).map((x, idx) => (
          <div key={idx}>{typeof x === "string" ? x : x?.title}</div>
        ));


      return (
        <div
          id="print-root"
           className={
              `rounded-2xl border border-gray-200 bg-white p-6 shadow-sm print:border-0 print:shadow-none
              ${this.state.hasSubscription === false ? "select-none" : ""}`
            }
        >
          <div className="prose max-w-none relative">
            <div className="text-3xl font-bold text-center text-black whitespace-pre-wrap">
              {s.practiceLetterhead}
            </div>

          
            <h2 className=" text-xl font-bold text-center">
              GP CHRONIC CONDITION MANAGEMENT PLAN
            </h2>
            <p className="text-sm text-gray-600 text-center">
              MBS Item 965/92029 or 392/92060
            </p>

            <div className="mt-4 grid grid-cols-2 md:grid-cols-2 gap-2 text-sm">
              <div>
                <strong>Name:</strong> {s.ptFullName || ""}
              </div>
              <div>
                <strong>Medicare No:</strong> {s.ptMCNo || ""}
              </div>
              <div>
                <strong>Date of Birth:</strong> {formatDate(s.ptDoB)}
              </div>
              <div>
                <strong>Identifies as Aboriginal or Torres Strait Islander:</strong>{" "}
                {s.isATSI || ""}
              </div>
              <div>
                <strong>Phone (M):</strong> {s.ptPhoneMob || ""}
              </div>
              <div>
                <strong>Phone (H):</strong> {s.ptPhoneH || ""}
              </div>
              <div className="md:col-span-2">
                <strong>Address:</strong>{" "}
                {[s.ptStreet, s.ptCity, s.ptPostcode, s.ptState]
                  .filter(Boolean)
                  .join(" ")}
              </div>
              <div className="md:col-span-2">
                <strong>Usual GP:</strong> {s.usualGP || ""}
              </div>
            </div>

            <h3 className="mt-4 font-semibold">Carer (if applicable)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
              <div>
                <strong>Name:</strong> {s.carerName || ""}
              </div>
              <div>
                <strong>Relationship:</strong> {s.carerRelationship || ""}
              </div>
              <div>
                <strong>Phone:</strong> {s.carerPhone || ""}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
              <div>
                <strong>Date of last GPCCMP/Review:</strong>{" "}
                {formatDate(s.lastGPCCMPDate)}
              </div>
              <div>
                <strong>Date service completed:</strong> {formatDate(s.todaysDate)}
              </div>
              <div>
                <strong>Proposed Review Date:</strong>{" "}
                {formatDate(s.proposedReviewDate)}
              </div>
            </div>

            <h3 className="mt-4 font-semibold">Other Notes</h3>
            <div className="text-sm whitespace-pre-wrap">{s.otherNotes}</div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <h4 className="font-semibold">Medical/Surgical History</h4>
                <div className="whitespace-pre-wrap">{s.selectedPMH}</div>
              </div>
              <div>
                <h4 className="font-semibold">Medications</h4>
                <div className="whitespace-pre-wrap">{s.selectedRx}</div>
              </div>
              <div>
                <h4 className="font-semibold">Allergies</h4>
                <div className="whitespace-pre-wrap">{s.reactions}</div>
              </div>
              <div>
                <h4 className="font-semibold">Social History</h4>
                <div className="whitespace-pre-wrap">{s.socialHx}</div>
              </div>
              <div>
                <h4 className="font-semibold">Family History</h4>
                <div className="whitespace-pre-wrap">{s.familyHx}</div>
              </div>
              <div>
                <h4 className="font-semibold">Current Progress Notes</h4>
                <div className="whitespace-pre-wrap">{s.todaysVisit}</div>
              </div>
            </div>

            <h3 className="mt-6 text-lg font-semibold text-center">
              Chronic Condition Management Plan
            </h3>
            <div className="mt-2 divide-y divide-gray-200 border border-gray-200 rounded-md overflow-hidden">
              <div className="grid grid-cols-4 gap-2 bg-gray-900 text-white px-3 py-2 text-sm font-medium">
                <div>Condition/Problem</div>
                <div>SMART Goals</div>
                <div>Tasks, required treatments and services</div>
                <div>Arrangements and referrals</div>
              </div>

              {sortedConds.map((c, i) => (
                <div
                  key={i}
                  className="grid grid-cols-4 md:grid-cols-4 gap-2 px-3 py-3 text-sm"
                >
                  <div className="whitespace-pre-wrap">
                    {c.problem}
                    {c.dateAdded && (
                      <div className="text-[10px] text-gray-500">
                        Added {formatDate(c.dateAdded)} • {c.author || "System"}
                      </div>
                    )}
                    <div className="whitespace-pre-wrap text-xs ml-2">
                      {(Array.isArray(c.notes) ? c.notes : []).map((n, idx) => (
                        <div key={idx}>{typeof n === "string" ? n : n?.title}</div>
                      ))}
                    </div>
                  </div>

                  <div className="whitespace-pre-wrap">
                    {renderActionList(c.goals)}
                  </div>

                  <div className="whitespace-pre-wrap">
                    {renderActionList(c.tasks)}
                  </div>

                  <div className="whitespace-pre-wrap">
                    {renderActionList(c.referrals)}
                  </div>
                </div>
              ))}
            </div>

            <p></p>
            <h3 className="mt-6 font-semibold">Acknowledgements & Consents</h3>
            <ul className="text-sm list-disc pl-5">
              <li>
                Copy of GPCCMP offered to patient: {s.copyOfferedToPatient ? "Yes" : "No"}
              </li>
              <li>
                GPCCMP added to the patient’s records: {s.addedToRecords ? "Yes" : "No"}
              </li>
              <li>
                Copy of relevant parts supplied to other providers: {s.copyToProviders ? "Yes" : "No"}
              </li>
              <li>
                The patient consents to the preparation of this Management Plan and agrees to the outlined goals. Patient gives permission for the GP to share medical history/diagnosis with other service providers: {s.consentShareHistory ? "Yes" : "No"}
              </li>
              <li>
                Patient gives permission for the management plan to be uploaded to My Health Record: {s.consentUploadMyHR ? "Yes" : "No"}
              </li>
            </ul>

            <div className="mt-4 text-sm">
              <strong>Date:</strong> {formatDate(s.todaysDate)}
              <p></p>

              {s.gpSignatureDataUrl ? (
                <img
                  src={s.gpSignatureDataUrl}
                  alt="GP signature"
                  className="h-16 object-contain"
                  style={{ imageRendering: "auto" }}
                />
              ) : (
                <>
                  <p></p>
                  <span>_______________________________</span>
                </>
              )}

              <div>
                <strong>GP Name:</strong> {s.gpName || ""}
              </div>
            </div>

            <h4 className="mt-4 font-semibold">Any information the patient wants withheld</h4>
            <div className="text-sm whitespace-pre-wrap">{s.withheldInfo}</div>
          </div>
        </div>
      );
    }

    renderPreviewTab() {


        return (
            <div className="space-y-4">


                <div className="flex justify-end gap-4 print:hidden">
                  <button
                    type="button"
                    onClick={ () => this.copyPreviewToClipboard('All')}
                    className="text-white rounded px-3 py-2 text-sm bg-sky-800 hover:bg-sky-900 shadow"
                    title="Copy the entire preview to your clipboard"
                  >
                  Copy Full Plan
                  </button>
                  <button
                    type="button"
                    onClick={ () => this.copyPreviewToClipboard('CDM')}
                    className="text-white rounded px-3 py-2 text-sm bg-sky-800 hover:bg-sky-900 shadow"
                    title="Copy the entire preview to your clipboard"
                  >
                  Copy Action Plan Only
                  </button>
                </div>
     
              
                {this.renderPreviewPanel()}
            </div>
        );
    }


    //////////////////////////////////
    //  RENDER DIFFERENT MODALS    ///
    //////////////////////////////////

    renderPasswordDialog() {
      if (!this.state.passwordDialogOpen) return null;

      return (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40"
          onMouseDown={(e) => { if (e.target === e.currentTarget) this._cancelPasswordDialog?.(); }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl p-4 w-[420px] max-w-[95vw]"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold">{this.state.passwordDialogTitle}</h3>
            {this.state.passwordDialogMessage && (
              <p className="text-sm text-gray-500 mt-1">{this.state.passwordDialogMessage}</p>
            )}

            <label className="block mt-3">
              <span className="text-sm font-medium text-gray-700">Password</span>
              <input
                ref={this.pwInputRef}
                type="password"
                className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/30"
                value={this.state.passwordDialogValue}
                onChange={(e) => this.setState({ passwordDialogValue: e.target.value, passwordDialogError: "" })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") this._confirmPasswordDialog?.();
                  if (e.key === "Escape") this._cancelPasswordDialog?.();
                }}
                placeholder="Enter password…"
                autoComplete="off"
              />
            </label>

            {this.state.passwordDialogError && (
              <div className="mt-2 text-sm text-red-600">{this.state.passwordDialogError}</div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border px-3 py-2 text-sm hover:bg-gray-50"
                onClick={() => this._cancelPasswordDialog?.()}
              >
                {this.state.passwordDialogCancelText}
              </button>
              <button
                type="button"
                className="rounded-xl px-3 py-2 text-sm text-white bg-black hover:bg-black/90"
                onClick={() => this._confirmPasswordDialog?.()}
              >
                {this.state.passwordDialogOkText}
              </button>
            </div>
          </div>
        </div>
      );
    }

    renderMergeModal() {
      if (!this.state.mergeOpen) return null;
      const s = this.state;
      const list = s.conditions || [];

      const isSel = (i) => s.mergeSelected.has(i);

      return (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40"
          onMouseDown={(e) => { if (e.target === e.currentTarget) this.closeMergeModal(); }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl p-4 flex flex-col w-[900px] max-w-[95vw] max-h-[90vh]"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2">Merge Conditions</h3>
            <label className="block mb-3">
              <span className="text-sm font-medium text-gray-700">Merge Into</span>
              <input
                type="text"
                className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-black/30"
                placeholder="write a problem/issue for this merge"
                value={s.mergeTitle}
                onChange={(e) => this.setState({ mergeTitle: e.target.value })}
              />
            </label>

            <div className="text-sm text-gray-600 mb-2">
              Click problems below to select/deselect for merging.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 overflow-auto pr-1"
                style={{ maxHeight: 420 }}>
              {list.map((c, i) => {
                const active = isSel(i);
                return (
                  <div
                    key={i}
                    onClick={() => this.toggleMergeSelect(i)}
                    className={[
                      "cursor-pointer rounded-xl border p-3 transition",
                      active
                        ? "bg-sky-50 border-sky-400 ring-1 ring-sky-400"
                        : "bg-slate-50 hover:bg-slate-100 border-gray-200"
                    ].join(" ")}
                  >
                    <div className="font-medium">{c?.problem || "(Untitled problem)"}</div>
                    {c?.notes?.length ? (
                      <div className="mt-1 text-xs text-gray-600 line-clamp-2">
                        {(c.notes || []).slice(0, 2).map((n, idx) => (
                          <div key={idx}>{typeof n === "string" ? n : n?.title}</div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-xl border px-3 py-2 text-sm hover:bg-gray-50"
                onClick={this.closeMergeModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-xl px-3 py-2 text-sm text-white bg-black hover:bg-black/90"
                onClick={this.performMerge}
              >
                Merge
              </button>
            </div>
          </div>
        </div>
      );
    }


      // --- change tab ---
    handleTabChange = (tab) => {
      this.setState({ activeTab: tab });
    };

    renderActiveTabContent = () => {
      const { activeTab } = this.state;

      switch (activeTab) {
        case "Plan":
          return this.renderPlanTab();

        case "Preview":
          return this.renderPreviewTab();

        case "Consent":
          return this.renderConsentTab();

        default:
          return null; // render nothing safely
      }
    };

    renderTabs() {
        const { activeTab } = this.state;
        return (
          <div className="flex border-b mb-4">
            <button
              type="button"
              onClick={() => this.handleTabChange("Plan")}
              className={
                "px-4 py-2 text-sm font-medium border-b-2 " +
                (activeTab === "Form"
                  ? "border-sky-600 text-sky-700"
                  : "border-transparent text-gray-500 hover:text-gray-700")
              }
            >
              Plan
            </button>
            <button
              type="button"
              onClick={() => this.handleTabChange("Consent")}
              className={
                "px-4 py-2 text-sm font-medium border-b-2 " +
                (activeTab === "Consent"
                  ? "border-sky-600 text-sky-700"
                  : "border-transparent text-gray-500 hover:text-gray-700")
              }
            >
              Consent
            </button>
            <button
              type="button"
              onClick={() => this.handleTabChange("Preview")}
              className={
                "px-4 py-2 text-sm font-medium border-b-2 " +
                (activeTab === "Preview"
                  ? "border-sky-600 text-sky-700"
                  : "border-transparent text-gray-500 hover:text-gray-700")
              }
            >
              Preview
            </button>
          </div>
        );
    }

    renderGPCCMP() {

      return (
        <>
        <div
          id="main"
          className="text-center col-span-6 text-3xl font-bold py-4 text-sky-900"
        >
          GP Chronic Condition Management Plan
        </div>
        {this.renderTabs()}
        {this.renderActiveTabContent()}
        </>
      )

    }

    render() {
        const s = this.state;

            const { accessLoading, accessAllowed, accessError, leadEmail, pageId } = this.state;

        // Still getting access from backend
        if (accessLoading) {
          return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
              <div className="rounded-2xl bg-white p-8 shadow ring-1 ring-slate-200 max-w-md w-full">
                <div className="text-lg font-bold text-slate-900">Checking access…</div>
                <div className="mt-2 text-sm text-slate-600">
                  Please wait while we verify your link.
                </div>
              </div>
            </div>
          );
        }
        
        // loading content when access allowed
        if (!accessAllowed) {
          return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
              <div className="rounded-2xl bg-white p-8 shadow ring-1 ring-slate-200 max-w-md w-full">
                <div className="text-lg font-bold text-slate-900">Tool locked</div>

                <div className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">
                  {accessError || "Access denied."}
                </div>

                {leadEmail ? (
                  <div className="mt-3 text-xs text-slate-500">
                    Email: <span className="font-semibold">{leadEmail}</span>
                  </div>
                ) : null}

                {pageId ? (
                  <div className="mt-1 text-xs text-slate-400">
                    Link ID: {pageId}
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={this.checkAccessOnServer}
                  className="mt-5 w-full rounded-lg bg-sky-700 px-4 py-2 text-white text-sm font-semibold hover:bg-sky-600"
                >
                  Re-check access
                </button>
              </div>
            </div>
          );
        }



        return (
          <div>
      
            <div id='joy-Start' className="min-h-screen bg-gray-50">
            {/* Page header / actions */}
            <header className="sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-gray-200">
            <div className="mx-auto max-w-6xl px-4 py-3 float-right print:hidden xs:flex-col sm:flex-col">
                {/* <h1 className="text-xl font-bold xs:text-center xs:text-sm xs:mb-4 sm:mb-4 md:text-center">GP Chronic Condition Management Plan</h1> */}
            
                <div className="flex gap-2">
                
                {this.state.hasDocOpen ? (
                <>
                    <button
                    onClick={this.handleClose}
                    className="rounded px-3 py-2 text-sm text-white bg-sky-800 hover:bg-sky-900 "
                    >
                    Close
                    </button>
                    <button
                      onClick={() => {
                        this.saveEncryptedCDV();     
                        this.saveDataToLocalStorage(); 
                      }}
                    className="rounded px-3 py-2 text-sm text-white bg-sky-800 hover:bg-sky-900 "
                    >
                    Save
                    </button>
                </>
                ) : (
                <>
                    <button
                    onClick={this.handleNew}
                    className="rounded px-3 py-2 text-sm text-white bg-sky-800 hover:bg-sky-900 "
                    >
                    New
                    </button>
                    <button
                    onClick={this.loadEncryptedCDV}
                    className="rounded px-3 py-2 text-sm text-white bg-sky-800 hover:bg-sky-900 "
                    >
                    Open
                    </button>
                </>
                )}


                    {/* Optional: also hide these until open */}
                    {this.state.hasDocOpen && this.state.hasSubscription && (
                        <>
                        <button onClick={this.printPlan} className="rounded px-3 py-2 text-sm text-white bg-sky-800 hover:bg-sky-900 ">
                            Print
                        </button>
                        </>
                    )}
                </div>    
           
                 

            </div>
            </header>

            {/* Content */}
    
            <main className="mx-auto w-auto px-4 py-6 gap-6 print:block">
            {!this.state.hasDocOpen ? (
                <div className="col-span-2 text-center text-lg text-gray-800 py-24">
             
                <h1 className='text-sky-800 font-bold text-3xl my-4'>GPCCMP Generator</h1>
                <p>No file loaded. Create new file or open file by clicking New or Open.</p>
  
                </div>
            ) : (
                <>
                {/* Left column – form inputs (tabbed) */}
                <section className="print:hidden">
                    <Tabs
                    value={this.state.activeMainTab}
                    onChange={(v) => this.setState({ activeMainTab: v })}
                    items={[
                        { label: "Patient Data", value: "patient" },
                        { label: "History", value: "history" },
                        { label: "GPCCMP", value: "gpccmp"},
                    ]}
                    />
                    {this.state.activeMainTab === "patient" && this.renderPatientDataTab()}
                    {this.state.activeMainTab === "history" && this.renderHistoryTab()}
                    {this.state.activeMainTab === "gpccmp" && this.renderGPCCMP()}
        
                </section>

                {/* Print-only preview */}
                <section aria-hidden="true" className="hidden print:block">
                    {this.renderPreviewPanel()}
                </section>
                </>
            )}
            </main>
        
           


            {/* Print styles */}
            <style>{`
            @media print {
                header, .print\\:hidden { display: none !important; }
                .print\\:block { display: block !important; }
                main { padding: 0 !important; }
                section { box-shadow: none !important; border: 0 !important; }
                /* Optional: tidy margins */
                @page { margin: 16mm; }
                /* Optional: prevent awkward breaks inside cards */
                .prose, .rounded-2xl { break-inside: avoid; }
            }
            `}</style>
        {this.renderMergeModal()}
        {this.renderPasswordDialog()}


        <ToastContainer />
 

        </div>
            
          </div>
        );

    }
}

export default GPCCMP
